import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGet } from "../../Hooks/useFetch";
import DataLoader from "../../Components/DataLoader";
import AlertMessage from "../../Components/AlertMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faIdCard,
  faGlasses,
  faEyeSlash,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import "./LicenceOptions.css";

const LicenceOptions = () => {
  const { mastRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [orderCard, setOrderCard] = useState(true);
  const [visualAids, setVisualAids] = useState(false);
  const [colourBlind, setColourBlind] = useState(false);
  const [eBilling, setEBilling] = useState(true);
  const [isLocked, setIsLocked] = useState(false);

  const [resFees, errFees, isLoadingFees, apiGetFees] = useGet();

  useEffect(() => {
    apiGetFees(`/api/licence/LicenceFees/${mastRef}`);
  }, []);

  useEffect(() => {
    if (resFees && resFees.hasExistingLicence && !resFees.hasMbl && resFees.hasPwc) {
      setIsLocked(true);
      setVisualAids(resFees.existingVisualAids);
      setColourBlind(resFees.existingColourBlind);
    }
    if (resFees) {
      setEBilling(resFees.eBilling !== false);
    }
  }, [resFees]);

  const handleNext = () => {
    navigate(`/applylicence/${mastRef}`, {
      state: {
        from: location,
        licenceOptions: {
          orderCard,
          visualAids,
          colourBlind,
          eBilling,
        },
      },
    });
  };

  if (isLoadingFees) {
    return (
      <div className="dataLoader-container">
        <DataLoader isLoading={true} />
      </div>
    );
  }

  if (errFees) {
    return (
      <div className="licence-options-container">
        <div className="licence-options-form">
          <AlertMessage message={errFees} />
          <button
            type="button"
            className="licence-back-btn"
            onClick={() =>
              navigate("/", { state: { from: location }, replace: true })
            }
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="licence-options-container">
      <div className="licence-options-form">
        <h1>Finalise Licence - Options</h1>
        <p className="licence-options-description">
          Please select the options you would like for your licence application.
        </p>

        <div className="licence-options-list">
          <label className="licence-option-item">
            <div className="option-info">
              <FontAwesomeIcon icon={faIdCard} className="option-icon" />
              <div>
                <span className="option-label">Order a Card</span>
                <span className="option-description">
                  Physical licence card will be mailed to your address
                </span>
              </div>
            </div>
            <div className="option-toggle">
              <input
                type="checkbox"
                checked={orderCard}
                onChange={(e) => setOrderCard(e.target.checked)}
                className="toggle-input"
                id="orderCard"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>

          <label className={`licence-option-item${isLocked ? " option-disabled" : ""}`}>
            <div className="option-info">
              <FontAwesomeIcon icon={faGlasses} className="option-icon" />
              <div>
                <span className="option-label">Visual Aids Required</span>
                <span className="option-description">
                  {isLocked
                    ? "This value is set from your existing boat licence"
                    : "Indicate if you require visual aids (e.g. glasses) while operating"}
                </span>
              </div>
            </div>
            <div className="option-toggle">
              <input
                type="checkbox"
                checked={visualAids}
                onChange={(e) => setVisualAids(e.target.checked)}
                className="toggle-input"
                id="visualAids"
                disabled={isLocked}
              />
              <span className={`toggle-slider${isLocked ? " toggle-disabled" : ""}`}></span>
            </div>
          </label>

          <label className={`licence-option-item${isLocked ? " option-disabled" : ""}`}>
            <div className="option-info">
              <FontAwesomeIcon icon={faEyeSlash} className="option-icon" />
              <div>
                <span className="option-label">Eyesight Colour Deficiency</span>
                <span className="option-description">
                  {isLocked
                    ? "This value is set from your existing boat licence"
                    : "Indicate if you have colour vision deficiency"}
                </span>
              </div>
            </div>
            <div className="option-toggle">
              <input
                type="checkbox"
                checked={colourBlind}
                onChange={(e) => setColourBlind(e.target.checked)}
                className="toggle-input"
                id="colourBlind"
                disabled={isLocked}
              />
              <span className={`toggle-slider${isLocked ? " toggle-disabled" : ""}`}></span>
            </div>
          </label>

          <label className="licence-option-item">
            <div className="option-info">
              <FontAwesomeIcon icon={faEnvelope} className="option-icon" />
              <div>
                <span className="option-label">E-Billing</span>
                <span className="option-description">
                  MAST prefers to use electronic billing (E-billing). If you want to receive future bills by mail, please turn off the E-billing option here.
                </span>
              </div>
            </div>
            <div className="option-toggle">
              <input
                type="checkbox"
                checked={eBilling}
                onChange={(e) => setEBilling(e.target.checked)}
                className="toggle-input"
                id="eBilling"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
        </div>

        <div className="licence-options-btn-group">
          <button
            type="button"
            className="licence-back-btn"
            onClick={() =>
              navigate("/", { state: { from: location }, replace: true })
            }
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </button>
          <button
            type="button"
            className="licence-next-btn"
            onClick={handleNext}
          >
            Next <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LicenceOptions;
